/**
 * /home — the front door for a person who is already here.
 *
 * The landing page (public/landing/index.html) sells Blitz to a stranger and forwards a signed-in
 * account here before it paints. Nobody with an account needs the pitch; they need the thing
 * itself laid out — a portal in the shape Newgrounds and Kongregate settled on, drawn for a
 * phone: what is new, what people liked, every kind with a count, who is making things, and your
 * own cards with the numbers the host measured. A stranger who finds /home gets the same
 * directory with the two doors in place of the account band; the catalogue stays the long list.
 *
 * Mounted by src/index.tsx like /you: no Carousel, no TelemetryTransport, no feed resource, no
 * window.__blitz. Opening it never starts a card session. Who is here is PEEKED, never minted.
 * This file imports nothing that can emit a row on load (tests/unit/join.test.ts pins the list);
 * the one row it can write is stats_open, when an account's numbers are shown, which is the last
 * step of the creation loop (docs/SPRINT-2.md section 2). Every door carries the visit's
 * attribution or ?via=home (src/lib/home.ts, attribution). The build with no backend shows the
 * bundled cards, or window.__blitzFixture.home when the e2e suite hands it rows.
 */
import { For, Show, createResource, createSignal, onMount } from 'solid-js';
import { ANONYMOUS, createAccountApi, type Account, type AccountClient } from '../api/account';
import { cardStanding, cardStats, logStatsOpen, myCards, statsSentence } from '../api/cards';
import { MOCK_GAMES } from '../api/mockGames';
import { resetSession, supabase } from '../backend/supabase';
import { HOME_COLUMNS, ago, attribution, cardHref, isRecent, kindCounts, kindOf, makerHref, makers, metaToRow, mostLiked, newest, type HomeRow } from '../lib/home';
import { PageNav } from './PageNav';

/** The session as it is, never minted. */
const peekSession = async () => (supabase ? (await supabase.auth.getSession()).data.session : null);
const peek = createAccountApi(supabase as unknown as AccountClient | null, peekSession, resetSession);

export type HomeView = 'loading' | 'stranger' | 'handle' | 'account';
export const homeViewFor = (a: Account): HomeView => (a.isAccount ? (a.handle ? 'account' : 'handle') : 'stranger');

interface Fixture {
  account?: Partial<Account>;
  home?: HomeRow[];
}
const fixture = (): Fixture | undefined => (!supabase ? (window as unknown as { __blitzFixture?: Fixture }).__blitzFixture : undefined);

/** Every live card, newest first — one read of card_public, the catalogue's columns. Null when it could not be read. */
async function loadRows(): Promise<HomeRow[] | null> {
  if (!supabase) {
    const fx = fixture()?.home;
    if (Array.isArray(fx)) return fx;
    // Dated a day apart, an hour back from now, so the shelf reads the same on any date the suite
    // runs (seven of them within the week, whatever the clock did between here and the render).
    return MOCK_GAMES.map((m, i) => metaToRow(m, new Date(Date.now() - i * 86_400_000 - 3_600_000).toISOString()));
  }
  const { data, error } = await supabase.from('card_public').select(HOME_COLUMNS).order('created_at', { ascending: false }).limit(1000);
  if (error || !Array.isArray(data)) return null;
  return data as unknown as HomeRow[];
}

const cleanAccent = (a: string | null) => (a ? `background:${String(a).replace(/[^#a-z0-9(),.% ]/gi, '')}` : undefined);

/** One card on a shelf: its thumbnail, its title, whose it is, and one word more. */
function Tile(props: { row: HomeRow; href: string; note?: string }) {
  const who = () => (props.row.platform ? `${props.row.developer || props.row.platform} · ${props.row.platform}` : props.row.developer ? `@${props.row.developer}` : '');
  return (
    <li class="tile">
      <a href={props.href} data-testid="home-card">
        <span class="thumb" style={cleanAccent(props.row.accent)}>
          <Show when={props.row.thumbnail_url}>
            <img src={props.row.thumbnail_url!} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.remove()} />
          </Show>
        </span>
        <b>{props.row.title || props.row.card_id}</b>
        <span class="by">
          {who()}
          <Show when={props.note}>
            <em> · {props.note}</em>
          </Show>
        </span>
      </a>
    </li>
  );
}

export function HomePage() {
  const search = location.search;
  const tag = attribution(search, 'home');
  const feed = `/feed${tag}`;
  const add = `/feed${tag}&link=1`;
  const join = `/join${tag}`;
  const signin = `/signin${tag}`;
  const now = Date.now();

  const [acct, setAcct] = createSignal<Account>(ANONYMOUS);
  const [view, setView] = createSignal<HomeView>('loading');
  const [rows] = createResource(loadRows);
  // Your cards and their numbers, only once we know there is an account with a handle behind
  // the session — the reads are authenticated RPCs and would be refused for anyone else.
  const [mine] = createResource(
    () => view() === 'account',
    async (ok) => (ok ? myCards() : []),
  );
  const [stats] = createResource(
    () => view() === 'account',
    async (ok) => {
      if (!ok) return new Map();
      const m = await cardStats(14);
      if (m.size > 0) logStatsOpen();
      return m;
    },
  );

  onMount(async () => {
    const fx = fixture()?.account;
    const a = fx ? { ...ANONYMOUS, ...fx } : await peek.load();
    setAcct(a);
    setView(homeViewFor(a));
    document.title = 'Home — Blitz';
  });

  const fresh = () => newest(rows() ?? [], 12);
  const liked = () => mostLiked(rows() ?? [], 12);
  const kinds = () => kindCounts(rows() ?? []);
  const people = () => makers(rows() ?? [], 12);
  const live = () => rows()?.length ?? 0;
  const recentCount = () => (rows() ?? []).filter((r) => isRecent(r, now)).length;
  const kindWord = (r: HomeRow) => kindOf(r).replace(/s$/, '').replace('photo', 'image');

  return (
    <div class="app">
      <PageNav current="home" feed={feed} signin={signin} isAccount={acct().isAccount} handle={acct().handle} feedTestId="home-feed-top" whoTestId="home-who" />

      <main class="home-page" data-testid="home-page" data-view={view()}>
        {/* The band at the top is the one thing that differs by who you are. */}
        <section class="home-band" data-testid="home-band">
          <Show when={view() === 'account'}>
            <h1 data-testid="home-hi">Hi, @{acct().handle}.</h1>
            <Show when={mine()} fallback={<p class="hint">Reading your cards…</p>}>
              {(list) => (
                <Show
                  when={list().length}
                  fallback={
                    <div class="home-offer" data-testid="home-offer">
                      <p class="hint">Nothing of yours is here yet. Ask a model for a page that fits a phone, or bring a picture, a clip or a site you built, and it waits for one look on a phone before strangers see it — under your handle, with the numbers.</p>
                      <div class="row">
                        <a class="btn primary" href={add} data-testid="home-add">
                          Add what you made
                        </a>
                      </div>
                    </div>
                  }
                >
                  <ul class="mine-list" data-testid="home-mine">
                    <For each={list()}>
                      {(c) => {
                        const s = () => stats()?.get(c.meta.id);
                        const open = c.status === 'live' || c.status === 'bench';
                        return (
                          <li data-status={c.status}>
                            <Show when={open} fallback={<span class="t">{c.meta.title}</span>}>
                              <a class="t" href={cardHref(c.meta.id, tag)}>
                                {c.meta.title}
                              </a>
                            </Show>
                            <span class="by">{cardStanding(c)}</span>
                            <Show when={s()}>{(st) => <span class="numbers" data-testid="home-numbers">{statsSentence(st())}</span>}</Show>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                  <p class="hint small">Measured by Blitz over the last 14 days: how many phones opened it, how many stayed five seconds or more, how many pressed on. Your own looks on this device are left out. Nothing a page says about itself is counted, and neither are likes.</p>
                  <div class="row">
                    <a class="btn primary" href={add} data-testid="home-add">
                      Add what you made
                    </a>
                    <a class="btn" href="/you" data-testid="home-profile">
                      Your profile
                    </a>
                  </div>
                </Show>
              )}
            </Show>
          </Show>
          <Show when={view() === 'handle'}>
            <h1>Pick your handle</h1>
            <p class="hint">Your handle is the name on everything you add. One more step.</p>
            <div class="row">
              <a class="btn primary" href="/you" data-testid="home-pick-handle">
                Pick it now
              </a>
            </div>
          </Show>
          <Show when={view() === 'stranger'}>
            <h1>The feed for what people make with AI.</h1>
            <p class="hint">Pictures, clips, pages and games, one tap apart, on a phone. Looking around never needs an account; adding something does.</p>
            <div class="row">
              <a class="btn primary" href={feed} data-testid="home-open">
                Open the feed
              </a>
              <a class="btn" href={join} data-testid="home-join">
                Create account
              </a>
            </div>
          </Show>
        </section>

        <p class="home-status" id="home-status" aria-live="polite" data-testid="home-status">
          <Show when={rows.loading}>Reading what is live…</Show>
          <Show when={!rows.loading && rows() === null}>
            <span class="err">The list could not load. The feed is still there.</span>
          </Show>
          <Show when={rows()}>
            {live()} {live() === 1 ? 'card' : 'cards'} live
            <Show when={recentCount() > 0}> · {recentCount()} new this week</Show>
          </Show>
        </p>

        <Show when={fresh().length > 0}>
          <section class="home-shelf" data-testid="home-new">
            <h2>
              New <a class="more" href="/catalogue">See everything</a>
            </h2>
            <ul class="home-row">
              <For each={fresh()}>{(r) => <Tile row={r} href={cardHref(r.card_id, tag)} note={isRecent(r, now) ? ago(r.created_at, now) : kindWord(r)} />}</For>
            </ul>
          </section>
        </Show>

        <Show when={liked().length > 0}>
          <section class="home-shelf" data-testid="home-liked">
            <h2>Most liked</h2>
            <ul class="home-row">
              <For each={liked()}>{(r) => <Tile row={r} href={cardHref(r.card_id, tag)} note={`♥ ${Number(r.likes)}`} />}</For>
            </ul>
          </section>
        </Show>

        <Show when={kinds().length > 0}>
          <section class="home-shelf" data-testid="home-kinds">
            <h2>By kind</h2>
            <ul class="chips">
              <For each={kinds()}>
                {(k) => (
                  <li>
                    <a href={`/catalogue#g-${k.key}`}>
                      {k.title} <span class="n">{k.count}</span>
                    </a>
                  </li>
                )}
              </For>
            </ul>
          </section>
        </Show>

        <Show when={people().length > 0}>
          <section class="home-shelf" data-testid="home-makers">
            <h2>People on Blitz</h2>
            <ul class="people">
              <For each={people()}>
                {(m) => {
                  const href = makerHref(m.handle);
                  const body = (
                    <>
                      <span class="ava" aria-hidden="true">
                        {m.handle[0].toUpperCase()}
                      </span>
                      <b>@{m.handle}</b>
                      <span class="by">
                        {m.count} {m.count === 1 ? 'card' : 'cards'}
                      </span>
                    </>
                  );
                  return <li>{href ? <a href={href}>{body}</a> : <span>{body}</span>}</li>;
                }}
              </For>
            </ul>
          </section>
        </Show>

        {/* The offer, for anyone who has not put something here: what a maker gets, all of it true today. */}
        <Show when={view() === 'stranger' || view() === 'handle'}>
          <section class="home-shelf home-offer" data-testid="home-offer">
            <h2>Made something with a model?</h2>
            <ul class="offer">
              <li>
                <b>A link that opens it playing on any phone.</b> No install, no account for the person you send it to.
              </li>
              <li>
                <b>Numbers measured by us, not reported by your page.</b> How many phones opened it, how many stayed, how many pressed on.
              </li>
              <li>
                <b>Your handle on every card, and a page of your own.</b> blitzfeed.app/@you, with everything you have added.
              </li>
            </ul>
            <div class="row">
              <a class="btn primary" href={join} data-testid="home-offer-join">
                Create account to add
              </a>
            </div>
          </section>
        </Show>

        <footer class="home-foot">
          <a href="/landing">About Blitz</a> · <a href="/catalogue">Catalogue</a> · <a href="/strategy">The strategy</a> · <a href="https://www.reddit.com/r/BlitzFeed/" rel="noopener">r/BlitzFeed</a>
        </footer>
      </main>
    </div>
  );
}
